import type Phaser from "phaser";
import { COLLISION_CELLS_PER_TILE, frameId, presenceArtCells, roomPlaneOf, type AssetManifest, type ChunkProp, type CompiledChunk, type FrameMaskOf, type ManifestObjectAppearance } from "daedalus-core";
import { PropView, type CullArea, type EaseStamp, type TileAnimator } from "daedalus-renderer-phaser";
import { PresencePlayback, inPresenceArea, presenceArea, roofedApart, type PresenceArea } from "odyssey-shared";
import { RoofPlace, type RoofSpec } from "./roof-place.ts";

/**
 * How far past the painted area's columns a roof's place reaches on each side, in collision cells: the area is painted on the floor
 * and the walls stand on the ring just outside it, so the place takes in the walls and leaves the roof's eaves, which overhang the
 * ground outside them.
 */
const WALL_OUTSET_CELLS = 1.5;

/**
 * The props this client drives by where its own character stands (ADR-058 amendment, P1.3-T4): a roof that
 * opens while the local character is under it, on this screen alone. The compiler keeps such a prop in its
 * chunk's prop layer carrying its appearance index; the scene hands those props here instead of to its own
 * prop views, and each frame hands the local character's drawn position, and this module plays each prop's
 * strips and shows the frame the playback answers. The props are drawn in prop views of their own, which the
 * occluder fade (ADR-040) is never handed, so the fade and the opening never both answer a character standing
 * under a roof. Every prop's area is resolved once, here, from the painted cells its appearance carries or,
 * with none, from the ground under its art in the world's asset manifest; a frame then allocates nothing.
 */

/**
 * A prop to drive: the layer it is drawn on and the prop as its chunk carries it.
 */
export interface PresenceEntry {
  readonly layerId: string;
  readonly prop: ChunkProp;
}

/**
 * One driven prop: its id and layer, its slot in the playback and its area.
 */
interface Driven {
  readonly id: string;
  readonly layerId: string;
  readonly slot: number;
  readonly area: PresenceArea;
}

/**
 * Whether a chunk prop is driven by presence: it carries the index of an appearance that names an inside
 * state.
 *
 * @param prop The prop as its chunk carries it.
 * @param appearances The world version's appearance table.
 * @returns True for a prop this client drives.
 */
export function drivenByPresence(prop: ChunkProp, appearances: readonly ManifestObjectAppearance[]): boolean {
  return prop.appearance !== undefined && appearances[prop.appearance]?.inside !== undefined;
}

/**
 * Every driven prop of a world's chunks, each with the layer it is drawn on.
 *
 * @param chunks The world's client chunks, each once.
 * @param appearances The world version's appearance table.
 * @returns The props to drive.
 */
export function presenceEntries(chunks: Iterable<CompiledChunk>, appearances: readonly ManifestObjectAppearance[]): PresenceEntry[] {
  const entries: PresenceEntry[] = [];
  for (const chunk of chunks) {
    for (const layer of chunk.layers) {
      if (layer.kind !== "prop") {
        continue;
      }
      for (const prop of layer.props) {
        if (drivenByPresence(prop, appearances)) {
          entries.push({ layerId: layer.id, prop });
        }
      }
    }
  }
  return entries;
}

/**
 * The presence props of a world.
 */
export class PresenceProps {
  private readonly playback: PresencePlayback;
  private readonly driven: Driven[] = [];
  private readonly place: RoofPlace;
  private readonly specs: RoofSpec[] = [];
  private readonly views = new Map<string, PropView>();
  private readonly areas: PresenceArea[] = [];
  private readonly inside: Uint8Array;

  /**
   * Resolves every driven prop's area and gives it a slot in the playback.
   *
   * @param scene The scene the props are drawn in.
   * @param assets The world's asset manifest.
   * @param appearances The world version's appearance table.
   * @param tileSize Tile size in pixels.
   * @param mapHeight Map height in tiles, for depth sorting.
   * @param animator The tile animator the other prop views follow.
   * @param entries The props to drive, each once.
   * @throws Error when a prop's frame is missing from the asset manifest.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly assets: AssetManifest,
    appearances: readonly ManifestObjectAppearance[],
    private readonly tileSize: number,
    private readonly mapHeight: number,
    private readonly animator: TileAnimator | null,
    entries: Iterable<PresenceEntry>,
  ) {
    this.playback = new PresencePlayback(appearances);
    const cellPixels = tileSize / COLLISION_CELLS_PER_TILE;
    for (const { layerId, prop } of entries) {
      const look = prop.appearance === undefined ? undefined : appearances[prop.appearance];
      if (look === undefined || look.inside === undefined || prop.appearance === undefined) {
        continue;
      }
      const rect = assets.frames[frameId(prop.frame.tileset, null, prop.frame.tile)];
      if (rect === undefined) {
        throw new Error(`asset manifest has no frame ${frameId(prop.frame.tileset, null, prop.frame.tile)} for prop ${prop.id}`);
      }
      const painted = look.states[look.initial]?.activation ?? [];
      const cells = painted.length > 0 ? painted : presenceArtCells(rect, cellPixels, prop.flipX, prop.flipY);
      const area = presenceArea(prop, cells);
      this.driven.push({ id: prop.id, layerId, slot: this.playback.add(prop.appearance), area });
      const footState = look.states[look.initial];
      const footFrame = footState === undefined ? null : frameId(footState.frame.tileset, null, footState.frame.tile);
      const footRect = footFrame === null ? undefined : assets.frames[footFrame];
      const outset = WALL_OUTSET_CELLS * cellPixels;
      this.specs.push({ id: prop.id, layerId, prop, footFrame, width: footRect?.w ?? 0, height: footRect?.h ?? 0, left: area.x0 * cellPixels - outset, right: (area.x0 + area.width) * cellPixels + outset });
      this.areas.push(area);
    }
    this.inside = new Uint8Array(this.areas.length);
    this.place = new RoofPlace(scene, tileSize, this.specs);
  }

  /**
   * How many props this client drives.
   *
   * @returns The count.
   */
  public get count(): number {
    return this.driven.length;
  }

  /**
   * The views the driven props are drawn in, one per layer, for the page's cover report.
   *
   * @returns The views.
   */
  public get propViews(): Iterable<PropView> {
    return this.views.values();
  }

  /**
   * How many strips have started playing, for the page's diagnostics.
   *
   * @returns The count.
   */
  public get started(): number {
    return this.playback.started;
  }

  /**
   * The view a layer's driven props are drawn in, made when the layer first has one in range.
   *
   * @param layerId The layer.
   * @returns The view.
   */
  private viewOf(layerId: string): PropView {
    let view = this.views.get(layerId);
    if (view === undefined) {
      view = new PropView(this.scene, this.assets, this.tileSize, this.mapHeight, this.animator);
      this.views.set(layerId, view);
    }
    return view;
  }

  /**
   * Draws a run of a layer's driven props, those of a chunk coming into the loaded range, in the layer's own depth
   * band, as the scene draws the layer's other props.
   *
   * @param layerId The layer.
   * @param props The list the run is in.
   * @param from Index of the run's first prop.
   * @param to Index after its last.
   * @param depth The layer's depth.
   * @throws Error when a prop's frame is missing from the asset manifest.
   */
  public enter(layerId: string, props: readonly ChunkProp[], from: number, to: number, depth: number): void {
    this.viewOf(layerId).enter(props, from, to, depth, true);
  }

  /**
   * Stops drawing a run of a layer's driven props, those of a chunk going out of the loaded range. They keep
   * playing, and show their frame when they come back.
   *
   * @param layerId The layer.
   * @param props The list the run is in.
   * @param from Index of the run's first prop.
   * @param to Index after its last.
   */
  public leave(layerId: string, props: readonly ChunkProp[], from: number, to: number): void {
    this.views.get(layerId)?.leave(props, from, to);
  }

  /**
   * Plays every driven prop to now for the local character standing at a feet point, and shows each the
   * frame its playback answers. A prop out of view keeps playing and shows its frame when it comes back.
   *
   * @param x The local character's feet x in sixteenths, as drawn.
   * @param y The local character's feet line in sixteenths, as drawn.
   * @param nowMs The client clock in milliseconds.
   */
  public update(x: number, y: number, nowMs: number): void {
    for (let i = 0; i < this.driven.length; i += 1) {
      const driven = this.driven[i] as Driven;
      const inside = inPresenceArea(driven.area, x, y);
      this.inside[i] = inside ? 1 : 0;
      this.playback.update(driven.slot, inside, nowMs);
      this.views.get(driven.layerId)?.showFrame(driven.id, this.playback.frame(driven.slot).id);
    }
    this.place.update(this.inside, x, y);
  }

  /**
   * Whether the local character stood in some driven prop's area at the last `update`: under a roof that is open on
   * this screen.
   *
   * @returns True when it did.
   */
  public get insideAny(): boolean {
    return this.inside.includes(1);
  }

  /**
   * Whether a character stands under a presence prop the local character is not under (ADR-061's amendment for
   * P1.9-T73): a roof drawn closed over it on this screen. Reads the inside answers the last `update` made.
   *
   * @param x The character's feet x in sixteenths, whole.
   * @param y The character's feet line in sixteenths, whole.
   * @returns True when such a prop's area holds it.
   */
  public roofedApart(x: number, y: number): boolean {
    return roofedApart(this.areas, this.inside, x, y);
  }

  /**
   * The room plane of the roofs on this map, by the compile's rule (`roomPlaneOf`): what shelters a character from the
   * rain (ADR-144's amendment of 2026-11-01), cell for cell the plane the map server reads from its sight planes.
   *
   * @param columns The map's width in authoring cells.
   * @param rows The map's height in authoring cells.
   * @returns The plane.
   */
  public roomPlane(columns: number, rows: number): Uint8Array {
    return roomPlaneOf(this.areas, columns, rows);
  }

  /**
   * Gives the roofs the room plane that decides who is sheltered and what the painted floor covers on the screen (`roomPlane`).
   *
   * @param plane The plane `roomPlane` made.
   * @param columns The map's width in authoring cells.
   * @param rows The map's height in authoring cells.
   */
  public useRooms(plane: Uint8Array, columns: number, rows: number): void {
    this.place.useRooms(plane, columns, rows);
  }

  /**
   * Gives the roofs the solid pixels of their frames, which give their place its height on the screen.
   *
   * @param maskOf The atlas's masks, or null when they could not be read, which leaves the room's cells alone as the places.
   */
  public useMasks(maskOf: FrameMaskOf | null): void {
    this.place.useMasks(maskOf);
  }

  /**
   * Whether the local character is sheltered by a roof as of the last `update`: `roofedAt` over the room plane, the answer the map
   * server and Drenched give.
   *
   * @returns True under a roof.
   */
  public get sheltered(): boolean {
    return this.place.isSheltered;
  }

  /**
   * The place the local character's roofs cover on the screen: where the rain and the wind are not drawn.
   *
   * @returns The place.
   */
  public get open(): CullArea {
    return this.place.open;
  }

  /**
   * The art stamps the light layer eases the darkness by, one for each roof that shelters the local character.
   *
   * @returns The stamps, the shared empty list when none.
   */
  public get stamps(): readonly EaseStamp[] {
    return this.place.stamps;
  }

  /**
   * The feathered outlines the soak is cut by, one for each roof that shelters the local character.
   *
   * @returns The stamps, the shared empty list when none.
   */
  public get softStamps(): readonly EaseStamp[] {
    return this.place.softStamps;
  }

  /**
   * Whether a world pixel lies in the place of any roof, open or not.
   *
   * @param x World x in pixels.
   * @param y World y in pixels.
   * @returns True when it does.
   */
  public coversAny(x: number, y: number): boolean {
    return this.place.coversAny(x, y);
  }

  /**
   * Whether every roof's place is final, so what `coversAny` drops is dropped for good.
   *
   * @returns True when nothing more will be read.
   */
  public get placesReady(): boolean {
    return this.place.ready;
  }

  /**
   * Removes the canvas textures the stamps were drawn to, when the page's stage ends.
   */
  public destroy(): void {
    this.place.destroy();
  }

  /**
   * Makes the next update first sight for every prop, as a welcome does, so a character who joins inside a
   * building finds it open with nothing played.
   */
  public reset(): void {
    this.playback.reset();
  }
}
